import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChildren,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  OnInit,
  output,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { HxSplitterHandle } from './splitter-handle';
import { PanelSizeSpec, SplitterOrientation, SplitterSizeChangeEvent } from './splitter.types';

/**
 * Harmony Splitter Component
 *
 * A resizable panel layout component that divides screen space into two adjustable sections with a draggable handle.
 * Ideal for side-by-side content viewing, audit interfaces, and responsive dashboard layouts. Uses design system tokens
 * for all styling values.
 *
 * **Features:**
 *
 * - Horizontal and vertical split orientations
 * - Draggable handle with visual feedback
 * - Keyboard navigation (Arrow keys, Home, End)
 * - Configurable size constraints (min/max in pixels)
 * - Touch support for mobile devices
 * - WCAG 2.2 AA accessible with ARIA attributes and screen reader announcements
 * - Smooth 60fps dragging with requestAnimationFrame throttling
 * - Panel collapse/expand functionality
 * - Programmatic size control via public API
 *
 * **Use Cases:**
 *
 * - Audit screens with assessment tree and content side-by-side
 * - Code editors with file explorer and editor panels
 * - Document viewers with table of contents navigation
 * - Dashboard layouts with resizable widget areas
 *
 * @example
 *   ```html
 *   <!-- Vertical split with 30% left panel -->
 *   <hx-splitter orientation="vertical" initialPanel1Size="30%" [minPanel1Size]="200">
 *     <ng-template>
 *       <div>Left Panel Content</div>
 *     </ng-template>
 *     <ng-template>
 *       <div>Right Panel Content</div>
 *     </ng-template>
 *   </hx-splitter>
 *
 *   <!-- Horizontal split with constraints -->
 *   <hx-splitter
 *     orientation="horizontal"
 *     initialPanel1Size="300px"
 *     [minPanel1Size]="200"
 *     [maxPanel1Size]="600"
 *     (sizeChange)="onResize($event)">
 *     <ng-template>
 *       <div>Top Panel</div>
 *     </ng-template>
 *     <ng-template>
 *       <div>Bottom Panel</div>
 *     </ng-template>
 *   </hx-splitter>
 *   ```;
 */
@Component({
  selector: 'hx-splitter',
  standalone: true,
  imports: [HxSplitterHandle, NgTemplateOutlet],
  templateUrl: './splitter.html',
  styleUrls: ['./splitter.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HxSplitter implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  // Content children - panel templates
  readonly templates = contentChildren<TemplateRef<any>>(TemplateRef);

  /**
   * The split orientation of the panels
   *
   * - 'vertical': Panels arranged side-by-side (left/right) - default
   * - 'horizontal': Panels stacked vertically (top/bottom)
   */
  readonly orientation = input<SplitterOrientation>('vertical');

  /**
   * Initial size of the first panel
   *
   * Accepts pixel values ('300px') or percentages ('40%')
   *
   * @example
   *   initialPanel1Size = '50%'; // 50% of container width/height
   *   initialPanel1Size = '300px'; // Fixed 300 pixels
   */
  readonly initialPanel1Size = input<string>('50%');

  /**
   * Minimum size of the first panel in pixels
   *
   * Prevents panel 1 from being resized below this value. Also affects the maximum size of panel 2.
   *
   * @default 100
   */
  readonly minPanel1Size = input<number>(100);

  /**
   * Maximum size of the first panel in pixels
   *
   * Prevents panel 1 from being resized above this value. Set to null for no maximum limit.
   *
   * @default null (unlimited)
   */
  readonly maxPanel1Size = input<number | null>(null);

  /**
   * Minimum size of the second panel in pixels
   *
   * Prevents panel 2 from being resized below this value. Also affects the maximum size of panel 1.
   *
   * @default 100
   */
  readonly minPanel2Size = input<number>(100);

  /**
   * Maximum size of the second panel in pixels
   *
   * Prevents panel 2 from being resized above this value. Set to null for no maximum limit.
   *
   * @default null (unlimited)
   */
  readonly maxPanel2Size = input<number | null>(null);

  /**
   * Enable panel collapse/expand functionality
   *
   * When true, panels can be programmatically collapsed to their minimum size and restored to their previous size.
   *
   * @default false
   */
  readonly collapsible = input<boolean>(false);

  /**
   * Resize amount per keyboard arrow key press in pixels
   *
   * Controls how much the panel size changes when using arrow keys for keyboard navigation.
   *
   * @default 5
   */
  readonly keyboardStep = input<number>(5);

  /**
   * Disable all resize interactions
   *
   * When true, the splitter handle becomes non-interactive and panels cannot be resized via mouse, touch, or keyboard.
   *
   * @default false
   */
  readonly disabled = input<boolean>(false);

  /**
   * Accessible label for the splitter handle
   *
   * Announced by screen readers to identify the resize control.
   *
   * @default 'Resize panels'
   */
  readonly ariaLabel = input<string>('Resize panels');

  /**
   * Restore user's last split position on reload
   *
   * When true, the component saves the panel size to localStorage whenever the user resizes, and restores it on the
   * next load. Requires storageKey to be set to a unique identifier.
   *
   * @default false
   */
  readonly rememberLastSize = input<boolean>(false);

  /**
   * Unique key for localStorage persistence
   *
   * Used when rememberLastSize is true to store and retrieve the last panel size. Should be unique per splitter
   * instance.
   *
   * @example
   *   ('audit-splitter', 'main-layout-splitter');
   *
   * @default null
   */
  readonly storageKey = input<string | null>(null);

  /**
   * Emitted when panel sizes change
   *
   * Fires during drag operations, keyboard resizing, and programmatic size changes. Provides both percentage and pixel
   * values.
   */
  readonly sizeChange = output<SplitterSizeChangeEvent>();

  /**
   * Emitted when drag operation starts
   *
   * Fires when user begins dragging the handle.
   */
  readonly dragStart = output<void>();

  /**
   * Emitted when drag operation ends
   *
   * Fires when user releases the handle after dragging.
   */
  readonly dragEnd = output<void>();

  // Internal state signals
  protected readonly panel1Percentage = signal<number>(50);
  protected readonly isDragging = signal<boolean>(false);
  protected readonly isPanel1Collapsed = signal<boolean>(false);
  protected readonly isPanel2Collapsed = signal<boolean>(false);
  protected readonly previousPanel1Size = signal<number | null>(null);
  protected readonly previousPanel2Size = signal<number | null>(null);
  protected readonly screenReaderMessage = signal<string>('');

  // View child reference to container
  protected readonly containerRef = viewChild<ElementRef>('containerRef');

  // Drag state
  private dragStartPosition = { x: 0, y: 0 };
  private dragStartPanelPercentage = 0;
  private rafId: number | null = null;

  // Computed signals
  protected readonly panel1Style = computed(() => ({
    'flex-basis': `${this.panel1Percentage()}%`,
  }));

  protected readonly panel2Style = computed(() => ({
    'flex-basis': `${100 - this.panel1Percentage()}%`,
  }));

  protected readonly flexDirection = computed<string>(() => (this.orientation() === 'vertical' ? 'row' : 'column'));

  protected readonly panel1PercentageSignal = computed<number>(() => Math.round(this.panel1Percentage()));

  protected readonly minPercentage = computed<number>(() => {
    const containerSize = this.getContainerSize();
    if (containerSize === 0) return 0;
    return Math.round((this.minPanel1Size() / containerSize) * 100);
  });

  protected readonly maxPercentage = computed<number>(() => {
    const containerSize = this.getContainerSize();
    if (containerSize === 0) return 100;

    const min2Pct = (this.minPanel2Size() / containerSize) * 100;
    const max1FromPanel2 = 100 - min2Pct;

    const max1 = this.maxPanel1Size();
    if (max1 !== null) {
      const max1Pct = (max1 / containerSize) * 100;
      return Math.round(Math.min(max1Pct, max1FromPanel2));
    }

    return Math.round(max1FromPanel2);
  });

  constructor() {
    // Setup cleanup for event listeners
    this.destroyRef.onDestroy(() => {
      this.cleanup();
    });

    // Re-apply constraints when min/max values change
    effect(() => {
      // Track all constraint inputs to trigger when they change
      const minPanel1 = this.minPanel1Size();
      const maxPanel1 = this.maxPanel1Size();
      const minPanel2 = this.minPanel2Size();
      const maxPanel2 = this.maxPanel2Size();

      // Re-validate current panel size against new constraints
      const currentPercentage = this.panel1Percentage();
      const constrainedPercentage = this.calculateConstrainedSize(currentPercentage);

      // Only update if the constrained value is different
      if (constrainedPercentage !== currentPercentage) {
        this.panel1Percentage.set(constrainedPercentage);
        this.emitSizeChange(false);
      }
    });
  }

  ngOnInit(): void {
    this.initializePanelSizes();
  }

  /** Parse size string into value and unit */
  private parseSize(sizeString: string): PanelSizeSpec {
    const match = sizeString.match(/^(\d+(?:\.\d+)?)(px|%)$/);
    if (!match) {
      throw new Error(`Invalid size format: ${sizeString}. Use '300px' or '40%'`);
    }
    return {
      value: parseFloat(match[1]),
      unit: match[2] as 'px' | '%',
    };
  }

  /** Initialize panel sizes from input */
  private initializePanelSizes(): void {
    try {
      // Check if we should restore from localStorage
      if (this.rememberLastSize() && this.storageKey()) {
        const savedSize = this.loadSizeFromStorage();
        if (savedSize !== null) {
          this.panel1Percentage.set(savedSize);
          return;
        }
      }

      const { value, unit } = this.parseSize(this.initialPanel1Size());

      if (unit === '%') {
        // Validate percentage is between 0-100, fallback to 50% if invalid
        const validatedValue = value >= 0 && value <= 100 ? value : 50;
        this.panel1Percentage.set(validatedValue);
      } else {
        // Convert pixels to percentage
        const containerSize = this.getContainerSize();
        if (containerSize > 0) {
          const percentage = (value / containerSize) * 100;
          // Validate calculated percentage
          const validatedPercentage = percentage >= 0 && percentage <= 100 ? percentage : 50;
          this.panel1Percentage.set(validatedPercentage);
        }
      }
    } catch (error) {
      console.error('Error initializing panel sizes:', error);
      this.panel1Percentage.set(50); // Fallback to 50%
    }
  }

  /** Get container size (width for vertical, height for horizontal) */
  private getContainerSize(): number {
    const container = this.containerRef()?.nativeElement;
    if (!container) return 0;

    return this.orientation() === 'vertical' ? container.offsetWidth : container.offsetHeight;
  }

  /** Handle mouse down on splitter handle */
  protected onHandleMouseDown(event: MouseEvent): void {
    if (this.disabled()) return;

    event.preventDefault();
    this.startDrag(event.clientX, event.clientY);

    const moveHandler = (e: MouseEvent) => this.onDragMove(e.clientX, e.clientY);
    const upHandler = () => {
      this.endDrag();
      document.removeEventListener('mousemove', moveHandler);
      document.removeEventListener('mouseup', upHandler);
    };

    document.addEventListener('mousemove', moveHandler);
    document.addEventListener('mouseup', upHandler);
  }

  /** Handle touch start on splitter handle */
  protected onHandleTouchStart(event: TouchEvent): void {
    if (this.disabled()) return;

    event.preventDefault();
    const touch = event.touches[0];
    this.startDrag(touch.clientX, touch.clientY);

    const moveHandler = (e: TouchEvent) => {
      const t = e.touches[0];
      this.onDragMove(t.clientX, t.clientY);
    };
    const endHandler = () => {
      this.endDrag();
      document.removeEventListener('touchmove', moveHandler);
      document.removeEventListener('touchend', endHandler);
    };

    document.addEventListener('touchmove', moveHandler, { passive: false });
    document.addEventListener('touchend', endHandler);
  }

  /** Handle keyboard input on splitter handle */
  protected onHandleKeyDown(event: KeyboardEvent): void {
    if (this.disabled()) return;

    const step = this.keyboardStep();
    let handled = false;

    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        this.adjustPanel1Size(-step);
        handled = true;
        break;
      case 'ArrowRight':
      case 'ArrowDown':
        this.adjustPanel1Size(step);
        handled = true;
        break;
      case 'Home':
        this.adjustPanel1Size(-Infinity); // Set to minimum
        handled = true;
        break;
      case 'End':
        this.adjustPanel1Size(Infinity); // Set to maximum
        handled = true;
        break;
    }

    if (handled) {
      event.preventDefault();
      this.announceResize();
      this.emitSizeChange(true);
    }
  }

  /** Start drag operation */
  private startDrag(x: number, y: number): void {
    this.dragStartPosition = { x, y };
    this.dragStartPanelPercentage = this.panel1Percentage();
    this.isDragging.set(true);
    this.dragStart.emit();
  }

  /** Update drag position with RAF throttling */
  private onDragMove(x: number, y: number): void {
    if (!this.isDragging()) return;

    if (this.rafId !== null) return; // Skip if frame already scheduled

    this.rafId = requestAnimationFrame(() => {
      const newSize = this.calculateSize(x, y);
      const constrainedSize = this.calculateConstrainedSize(newSize);
      this.panel1Percentage.set(constrainedSize);
      this.rafId = null;
    });
  }

  /** End drag operation */
  private endDrag(): void {
    this.isDragging.set(false);
    this.dragEnd.emit();
    this.announceResize();
    this.emitSizeChange(true);

    // Save to localStorage if rememberLastSize is enabled
    this.saveSizeToStorage(this.panel1Percentage());
  }

  /** Calculate new size based on cursor position */
  private calculateSize(x: number, y: number): number {
    const containerSize = this.getContainerSize();
    if (containerSize === 0) return this.panel1Percentage();

    const isVertical = this.orientation() === 'vertical';
    const delta = isVertical ? x - this.dragStartPosition.x : y - this.dragStartPosition.y;

    const deltaPercentage = (delta / containerSize) * 100;
    return this.dragStartPanelPercentage + deltaPercentage;
  }

  /** Apply constraints to requested size */
  private calculateConstrainedSize(requestedPercentage: number): number {
    const containerSize = this.getContainerSize();
    if (containerSize === 0) return requestedPercentage;

    // Convert pixel constraints to percentages
    const min1Pct = (this.minPanel1Size() / containerSize) * 100;
    const min2Pct = (this.minPanel2Size() / containerSize) * 100;

    // Panel 2's minimum constrains Panel 1's maximum
    const max1FromPanel2 = 100 - min2Pct;

    // Apply max constraint if specified
    let effectiveMax = max1FromPanel2;
    const max1 = this.maxPanel1Size();
    if (max1 !== null) {
      const max1Pct = (max1 / containerSize) * 100;
      effectiveMax = Math.min(max1Pct, max1FromPanel2);
    }

    // Clamp to bounds
    return Math.max(min1Pct, Math.min(requestedPercentage, effectiveMax));
  }

  /** Adjust panel 1 size by delta pixels */
  private adjustPanel1Size(deltaPixels: number): void {
    const containerSize = this.getContainerSize();
    if (containerSize === 0) return;

    const deltaPercentage = (deltaPixels / containerSize) * 100;
    const newPercentage = this.panel1Percentage() + deltaPercentage;
    const constrainedPercentage = this.calculateConstrainedSize(newPercentage);

    this.panel1Percentage.set(constrainedPercentage);
  }

  /** Announce resize to screen readers */
  private announceResize(): void {
    const p1Pct = Math.round(this.panel1Percentage());
    const p2Pct = 100 - p1Pct;
    this.screenReaderMessage.set(`Panel 1: ${p1Pct}%, Panel 2: ${p2Pct}%`);
  }

  /** Emit size change event */
  private emitSizeChange(userInitiated: boolean): void {
    const containerSize = this.getContainerSize();
    const panel1Pct = this.panel1Percentage();
    const panel2Pct = 100 - panel1Pct;

    this.sizeChange.emit({
      panel1Percentage: panel1Pct,
      panel2Percentage: panel2Pct,
      panel1Pixels: (panel1Pct / 100) * containerSize,
      panel2Pixels: (panel2Pct / 100) * containerSize,
      userInitiated,
      timestamp: Date.now(),
    });
  }

  /** Cleanup event listeners and RAF */
  private cleanup(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  // Public API methods

  /** Collapse panel 1 to minimum size */
  collapsePanel1(): void {
    if (!this.collapsible()) return;
    this.previousPanel1Size.set(this.panel1Percentage());
    this.panel1Percentage.set(this.minPercentage());
    this.isPanel1Collapsed.set(true);
    this.announceResize();
    this.emitSizeChange(false);
  }

  /** Expand panel 1 to previous size */
  expandPanel1(): void {
    if (!this.collapsible()) return;
    const previous = this.previousPanel1Size();
    if (previous !== null) {
      this.panel1Percentage.set(previous);
    }
    this.isPanel1Collapsed.set(false);
    this.announceResize();
    this.emitSizeChange(false);
  }

  /** Collapse panel 2 to minimum size */
  collapsePanel2(): void {
    if (!this.collapsible()) return;
    this.previousPanel2Size.set(this.panel1Percentage());
    this.panel1Percentage.set(this.maxPercentage());
    this.isPanel2Collapsed.set(true);
    this.announceResize();
    this.emitSizeChange(false);
  }

  /** Expand panel 2 to previous size */
  expandPanel2(): void {
    if (!this.collapsible()) return;
    const previous = this.previousPanel2Size();
    if (previous !== null) {
      this.panel1Percentage.set(previous);
    }
    this.isPanel2Collapsed.set(false);
    this.announceResize();
    this.emitSizeChange(false);
  }

  /** Programmatically set panel 1 size */
  setPanel1Size(size: string): void {
    try {
      const { value, unit } = this.parseSize(size);

      if (unit === '%') {
        const constrained = this.calculateConstrainedSize(value);
        this.panel1Percentage.set(constrained);
      } else {
        const containerSize = this.getContainerSize();
        if (containerSize > 0) {
          const percentage = (value / containerSize) * 100;
          const constrained = this.calculateConstrainedSize(percentage);
          this.panel1Percentage.set(constrained);
        }
      }

      this.announceResize();
      this.emitSizeChange(false);
    } catch (error) {
      console.error('Error setting panel size:', error);
    }
  }

  /** Reset panels to initial sizes */
  resetSizes(): void {
    this.initializePanelSizes();
    this.announceResize();
    this.emitSizeChange(false);
  }

  /** Get current panel sizes */
  getCurrentSizes(): {
    panel1Percentage: number;
    panel2Percentage: number;
    panel1Pixels: number;
    panel2Pixels: number;
  } {
    const containerSize = this.getContainerSize();
    const panel1Pct = this.panel1Percentage();
    const panel2Pct = 100 - panel1Pct;

    return {
      panel1Percentage: panel1Pct,
      panel2Percentage: panel2Pct,
      panel1Pixels: (panel1Pct / 100) * containerSize,
      panel2Pixels: (panel2Pct / 100) * containerSize,
    };
  }

  /** Save current panel size to localStorage */
  private saveSizeToStorage(percentage: number): void {
    if (!this.rememberLastSize() || !this.storageKey()) {
      return;
    }

    try {
      const key = `hx-splitter-${this.storageKey()}`;
      localStorage.setItem(key, percentage.toString());
    } catch (error) {
      console.warn('Failed to save splitter size to localStorage:', error);
    }
  }

  /** Load saved panel size from localStorage */
  private loadSizeFromStorage(): number | null {
    // Load called; skip if feature disabled or no storage key
    if (!this.rememberLastSize() || !this.storageKey()) {
      return null;
    }

    try {
      const key = `hx-splitter-${this.storageKey()}`;
      const saved = localStorage.getItem(key);
      if (saved !== null) {
        const percentage = parseFloat(saved);
        if (!isNaN(percentage) && percentage >= 0 && percentage <= 100) {
          return percentage;
        }
      }
    } catch (error) {
      console.warn('Failed to load splitter size from localStorage:', error);
    }

    return null;
  }
}
